import { useCallback, useMemo, useState } from 'react'
import { AlertCircle, Check, Loader2, Pencil, X } from 'lucide-react'
import { toast } from '@atrvd/saas-ui'
import dayjs from '@dcp/shared/utils/dayjs'
import {
  Badge,
  Button,
  DataTable,
  type DataTableColumn,
  DatePicker,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type DataTableSortState,
} from '@atrvd/saas-ui'

import {
  useSageChecks,
  useSageChecksCount,
  useUpdateSageCheck,
  useSageBankAccounts,
} from '@/hooks/useSageIntegration'
import { useBankingKpi, useFacilities } from '@/hooks/useBanking'
import { useTableColumnPrefs } from '@/hooks/useTableColumnPrefs'
import { pageOrCountError, queryErrorDisplay, tableQueryState } from '@/components/common/queryErrorDisplay'
import { useTablePagination } from '@/hooks/useTablePagination'
import { KpiCards } from '@/components/banking'
import { formatCurrency, formatDate, formatDateTime } from '@dcp/shared/utils/formatters'
import {
  OUTSTANDING_CHECK_STATES,
  effectiveCheckStatus,
  canAssignStatus,
  resolveDateCashed,
} from '@/lib/sage-utils'
import type { SageCheckRow, SageCheckSortColumn, SageBankAccountRow } from '@/lib/db-tenant'
import { DCP_CHECK_STATUSES, type DcpCheckStatus } from '@/lib/db-tenant'
import {
  ALL_STATUSES_VALUE,
  nextStatusSelection,
  toStatusSelectValue,
} from './statusFilterSelection'

/**
 * Outstanding Checks page (DCP-134 + DCP-133).
 *
 * Per the BA mapping spec, an "Outstanding Check" is one that has been Posted
 * in Sage Intacct (printed and sent — STATE='C'). Bank-clearance is tracked
 * DCP-side via the `date_cashed` column (manual entry by the AP team) until
 * automatic Plaid debit matching lands. Setting `date_cashed` flips the row's
 * Status to "Paid"; per DCP-237 the row stays in this list — the table shows
 * checks in every status (Issued, Paid, Voided). Only the Outstanding Checks
 * KPI tile remains issued-only, via the separate `sage_checks_outstanding_total`
 * RPC (see useBanking.ts), so the totals stay accurate independently of the list.
 *
 * Built on saas-ui DataTable: server-side sort + pagination + inline edit
 * for Date Cashed. Quick filters (Date Issued range, Facility, Status) sit
 * above the table and feed into the same query parameters.
 *
 * Bank Account # and Facility columns are joined client-side via lookup
 * maps over sage_bank_accounts and facilities — the underlying tables are
 * small (per-tenant catalogs) so a full prefetch is cheaper than threading
 * a PostgREST join. The Facility column resolves from the row's denormalized
 * `facility_id` (DCP-308); the Bank Account # column still resolves via
 * `financial_entity` → sage_bank_accounts.
 */

/** Display labels for the DCP status override. */
const STATUS_LABEL: Record<DcpCheckStatus, string> = {
  issued: 'Issued',
  paid: 'Paid',
  voided: 'Voided',
}

/**
 * Options of the Status quick filter: an explicit "All" first, then the
 * statuses with the same labels as the Status badge.
 */
const STATUS_FILTER_OPTIONS = [
  { value: ALL_STATUSES_VALUE, label: 'All' },
  ...DCP_CHECK_STATUSES.map((status) => ({ value: status, label: STATUS_LABEL[status] })),
]

/**
 * Server sort column for table columns whose key is not the DB column they
 * display. The Status column keeps key `state` (saved column prefs use it), but
 * every listed check has `state = 'C'`, so it must sort by the DCP status.
 */
const SORT_COLUMN_BY_KEY: Partial<Record<string, SageCheckSortColumn>> = {
  state: 'dcp_check_status',
}

/** Badge variant per effective DCP status. */
function statusBadgeVariant(
  status: DcpCheckStatus,
): 'secondary' | 'outline' | 'destructive' | 'default' {
  if (status === 'voided') return 'destructive'
  if (status === 'paid') return 'default'
  return 'outline' // issued
}

/**
 * Status cell for the Outstanding Checks table (DCP-275).
 *
 * Two modes, driven by the page-level row edit state:
 *  - Display: the effective status as a Badge plus a hover-revealed pencil
 *    (mirrors EditableBalanceCell) so it is evident the field is editable
 *    (AC#3). Clicking the pencil puts the WHOLE row — Status + Date Cashed —
 *    into edit mode, because the two fields are saved together.
 *  - Edit: a Select over every status. All options are always enabled; the
 *    cross-field rule (Paid needs a date) is enforced on Save, not by hiding
 *    options, so the user can switch between all statuses freely (AC#2).
 */
function StatusCell({
  row,
  isEditing,
  draftStatus,
  onDraftStatusChange,
  onBeginEdit,
  isPending,
}: {
  row: SageCheckRow
  isEditing: boolean
  draftStatus: DcpCheckStatus
  onDraftStatusChange: (status: DcpCheckStatus) => void
  onBeginEdit: (row: SageCheckRow) => void
  isPending: boolean
}) {
  if (isEditing) {
    return (
      <Select value={draftStatus} onValueChange={(v) => onDraftStatusChange(v as DcpCheckStatus)}>
        <SelectTrigger className="h-8 w-[100px]" aria-label="Check status">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DCP_CHECK_STATUSES.map((option) => (
            <SelectItem key={option} value={option}>
              {STATUS_LABEL[option]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  const status = effectiveCheckStatus(row)
  return (
    <div className="group flex items-center gap-1">
      <Badge variant={statusBadgeVariant(status)}>{STATUS_LABEL[status]}</Badge>
      {/* Hover-revealed edit affordance (AC#3) — opacity toggle copied from
          EditableBalanceCell so editability is discoverable without clutter. */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => onBeginEdit(row)}
        disabled={isPending}
        aria-label="Edit status and date cashed"
        className="h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
      >
        <Pencil className="h-3.5 w-3.5" />
      </Button>
    </div>
  )
}

/**
 * Date Cashed cell for the Outstanding Checks table (DCP-275).
 *
 * Shares the row edit mode with StatusCell and additionally owns the Save /
 * Cancel controls, because the cross-field rule lives on this field: a "Paid"
 * status requires a Date Cashed.
 *  - Display: the date is shown ONLY for Paid checks; Issued/Voided render an
 *    em-dash. Hover reveals the same pencil affordance.
 *  - Edit: the DatePicker appears ONLY when the draft status is "Paid" (Date
 *    Cashed belongs to a cashed check); any other status shows an em-dash and is
 *    not editable, and the date is cleared on save (DCP-275). When "Paid" has no
 *    date yet, the field is highlighted as required and Save is disabled (AC#2).
 */
function DateCashedCell({
  row,
  isEditing,
  draftStatus,
  draftDateCashed,
  onDraftDateCashedChange,
  onBeginEdit,
  onSave,
  onCancel,
  saveDisabled,
  isPending,
}: {
  row: SageCheckRow
  isEditing: boolean
  draftStatus: DcpCheckStatus
  draftDateCashed: string | null
  onDraftDateCashedChange: (dateCashed: string | null) => void
  onBeginEdit: (row: SageCheckRow) => void
  onSave: (row: SageCheckRow) => void
  onCancel: () => void
  saveDisabled: boolean
  isPending: boolean
}) {
  // The value shown depends on mode: the live draft while editing, the stored
  // value otherwise. Guard against an unparseable legacy value — passing an
  // Invalid Date into the saas-ui DatePicker would throw inside react-day-picker
  // and formatDate would render "Invalid Date". Treat unparseable as "no date".
  const value = isEditing ? draftDateCashed : row.date_cashed
  const selected = useMemo(() => {
    if (!value) return undefined
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? undefined : d
  }, [value])

  if (isEditing) {
    // Date Cashed is editable ONLY for 'paid' — a cashed check records WHEN it
    // cleared. 'issued'/'voided' have no Date Cashed (it is cleared on save), so
    // the picker is replaced by a non-editable em-dash (DCP-275).
    const editable = draftStatus === 'paid'
    // 'paid' with no date yet is the one blocking state: highlight the field and
    // let the page-computed `saveDisabled` gate the Save button (AC#2).
    const required = editable && !draftDateCashed
    return (
      <div className="flex items-center gap-1">
        {editable ? (
          // The picker stays in normal flow while the "required" affordances are
          // kept out of it (DCP-289): a destructive ring (not a border) and an
          // absolutely-positioned hint add no height, so the row never grows and
          // the field never jumps out of alignment with the Status cell.
          <div className="relative">
            <div className={required ? 'rounded-md ring-1 ring-destructive' : undefined}>
              <DatePicker
                aria-label="Date Cashed"
                date={selected}
                onDateChange={(date) =>
                  onDraftDateCashedChange(date ? dayjs(date).format('YYYY-MM-DD') : null)
                }
                placeholder={required ? 'Required' : 'Pick a date'}
                // 30% narrower than the 200px default (DCP-275): the column is
                // fixed-width and only needs room for a short ISO date; the 260px
                // column reserve still fits this picker plus Save/Cancel when the
                // table overflows to its min-width (DCP-289).
                className="sm:w-[140px]"
              />
            </div>
            {required && (
              <span className="absolute left-1/2 top-full mt-0.5 -translate-x-1/2 whitespace-nowrap text-xs text-destructive">
                Required for Paid
              </span>
            )}
          </div>
        ) : (
          // Non-'paid' status: Date Cashed is not applicable; it is cleared on save.
          <span className="text-muted-foreground" aria-label="Date Cashed not applicable">
            —
          </span>
        )}
        {/* Save / Cancel — mirrors EditableBalanceCell's check/x controls. */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onSave(row)}
          disabled={saveDisabled}
          aria-label="Save check"
          className="h-6 w-6"
        >
          {isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onCancel}
          disabled={isPending}
          aria-label="Cancel edit"
          className="h-6 w-6"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    )
  }

  // Date Cashed is shown only for Paid checks (DCP-275); Issued/Voided → em-dash.
  const isPaid = effectiveCheckStatus(row) === 'paid'
  const display = isPaid && selected ? formatDate(row.date_cashed as string) : '—'
  return (
    <div className="group flex items-center gap-1">
      <span className={isPaid && selected ? '' : 'text-muted-foreground'}>{display}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => onBeginEdit(row)}
        disabled={isPending}
        aria-label="Edit status and date cashed"
        className="h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
      >
        <Pencil className="h-3.5 w-3.5" />
      </Button>
    </div>
  )
}

/**
 * Convert a Date to an ISO date string (YYYY-MM-DD) for Postgres date filters.
 * Returns undefined when the input is undefined OR is an Invalid Date — without
 * the `isValid()` guard, `new Date('garbage')` would slip past the `if (!d)`
 * check (`!new Date('garbage')` is `false`) and dayjs.format() would return
 * the literal string `'Invalid Date'`, which Postgres would then reject as a
 * malformed timestamp.
 */
function toDateOnlyIso(d: Date | undefined): string | undefined {
  if (!d || !dayjs(d).isValid()) return undefined
  return dayjs(d).format('YYYY-MM-DD')
}

export function OutstandingChecksPage() {
  // ============ State ============

  /**
   * Server-mode sort state. Single-column for now — DataTable supports
   * multi-column with Ctrl+Click but the BA spec only asks for ASC/DESC
   * toggling on a single column at a time.
   */
  const [sortState, setSortState] = useState<DataTableSortState[]>([])

  /**
   * Quick filter: Date Issued range, filters by `when_paid`. Both bounds are
   * independently optional (unlike saas-ui's `DateRange`, where `from` is
   * required) so the From/To DatePickers can each be set or cleared on their
   * own; `undefined` overall means no date filter (all checks). DCP-238.
   */
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date } | undefined>()

  /**
   * Quick filter: tenant facility multi-select. Passed straight through to the
   * query as `facilityIds` — filtered on the denormalized
   * `sage_checks.facility_id` column (DCP-308), no bank-account resolution.
   */
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>([])

  /**
   * Quick filter: effective check status (what the Status badge shows). Empty
   * means every status — the page's default, shown as the "All" option; the
   * MultiSelect value is mapped to and from this by `statusFilterSelection.ts`.
   */
  const [selectedStatuses, setSelectedStatuses] = useState<DcpCheckStatus[]>([])

  // ============ Data ============

  // `??` not `=` because the hooks return { data: null } (not undefined)
  // during loading; the destructuring default only kicks in for undefined.
  // Wrapped in useMemo to keep array identity stable across renders so
  // downstream useMemos don't churn on every render.
  const facilitiesQuery = useFacilities()
  const facilities = useMemo(() => facilitiesQuery.data ?? [], [facilitiesQuery.data])
  const bankAccountsQuery = useSageBankAccounts({ activeOnly: false })
  const bankAccounts = useMemo(
    () => bankAccountsQuery.data ?? [],
    [bankAccountsQuery.data],
  )

  // The mapped-but-no-bank-account banner reads `bankAccounts` — a SEPARATE query
  // from the checks list. Only trust an EMPTY result once that query has actually
  // resolved: otherwise a fast/cached checks query paired with a still-loading (or
  // errored) bank-accounts query leaves bankAccounts = [], and every mapped
  // selected facility would falsely trip the banner (DCP-308 review #2).
  const bankAccountsReady = !bankAccountsQuery.isLoading && !bankAccountsQuery.isError

  /**
   * Aggregated balance tiles (DCP-274): the same KpiCards row shown on every
   * other Banking tab (Dashboard / Balance Import / Transactions Import).
   * Scoped to the page's Facility filter so the tiles track the active
   * selection. The page's Date Issued filter is intentionally NOT applied —
   * those tiles are balance figures (latest / as-of), conceptually distinct
   * from a check's issue date, so feeding the check filter into them would be
   * misleading. No filter → org-wide latest balances, matching the dashboard.
   */
  const kpiFilters = useMemo(
    () => ({ facilityIds: selectedFacilityIds.length ? selectedFacilityIds : undefined }),
    [selectedFacilityIds],
  )
  const { data: kpi, isLoading: kpiLoading } = useBankingKpi(kpiFilters)

  /**
   * Facility filter passed straight to the query (DCP-308): every selected
   * facility contributes to `sage_checks.facility_id IN (...)` regardless of
   * whether it has a synced bank account. `undefined` when nothing is selected
   * so the hook treats it as "all facilities" rather than "no rows".
   */
  const facilityIds = selectedFacilityIds.length ? selectedFacilityIds : undefined

  /**
   * Names of selected facilities that have no `sage_location_id` configured.
   *
   * Surfaced as an inline Alert above the table (see render path below) so
   * the operator can distinguish two states that otherwise look identical:
   *
   *   1. "This facility has 0 outstanding checks" — a legitimate empty
   *      result the user can act on (file the next sync, look at another
   *      facility).
   *   2. "This facility has no Sage Location ID mapping configured" — a
   *      *configuration* gap that requires an admin to fill the field in
   *      Settings → Facilities. Without this signal the user sees the
   *      same "No outstanding checks match the current filters." message
   *      and assumes the data is missing, double-working an issue that's
   *      actually on the admin side.
   *
   * Inline Alert (rather than a transient toast) is the right surface
   * because the message is *persistent* — it tells the operator the
   * filter is doing something they may not have expected, and stays
   * visible until they change the selection. A toast that auto-dismisses
   * after 4s would be missed if the user was reading the table.
   */
  const facilitiesWithoutSageMapping = useMemo(() => {
    if (selectedFacilityIds.length === 0) return []
    return facilities
      .filter((f) => selectedFacilityIds.includes(f.id) && !f.sage_location_id)
      .map((f) => f.name)
  }, [selectedFacilityIds, facilities])

  /**
   * Selected facilities that DO have a `sage_location_id` but map to ZERO
   * bank accounts (DCP-308). The mapping exists, yet no `sage_bank_accounts`
   * row carries a matching `location_id` (case-insensitive).
   *
   * Since Stage 2c the facility FILTER no longer depends on this resolution —
   * checks are filtered by `facility_id` directly (no silent no-op). But a
   * facility with no bank account has no checks ATTRIBUTED to it: `facility_id`
   * is stamped at sync time via financial_entity → bank account → location, so
   * with no bank account no check ever resolves to it. Selecting such a facility
   * therefore shows no rows until the operator runs Sync Bank Accounts (then
   * Sync Checks) for that Sage location. (Any pre-existing rows — from a bank
   * account since removed — would render "—" in the Bank Account # column.)
   * Unlike `facilitiesWithoutSageMapping` (a missing mapping the admin must
   * add), this is a data-sync gap. Surfaced as its own line so the two states
   * never read as one; each entry keeps the facility name and the mapped Sage
   * location id for an actionable message.
   */
  const facilitiesMappedWithoutBankAccount = useMemo(() => {
    if (selectedFacilityIds.length === 0) return []
    // Set of bank-account location ids, upper-cased, for a case-insensitive
    // membership test (both sides are normalized because the admin-typed
    // sage_location_id may differ in case from Sage's canonical location_id).
    const bankLocationIds = new Set(
      bankAccounts
        .filter((ba) => ba.location_id != null)
        .map((ba) => (ba.location_id as string).toUpperCase()),
    )
    return facilities
      .filter(
        (f) =>
          selectedFacilityIds.includes(f.id) &&
          f.sage_location_id &&
          !bankLocationIds.has((f.sage_location_id as string).toUpperCase()),
      )
      .map((f) => ({ id: f.id, name: f.name, sageLocationId: f.sage_location_id as string }))
  }, [selectedFacilityIds, facilities, bankAccounts])

  // Server-mode sort: pluck the active column + direction (single-column).
  // SortDirection includes `null` when the user toggles a column off; treat
  // that as "no sort" so the DB query falls back to the default order.
  const activeSort = sortState[0]
  const sortColumn =
    activeSort && activeSort.direction !== null
      ? (SORT_COLUMN_BY_KEY[activeSort.columnKey] ?? (activeSort.columnKey as SageCheckSortColumn))
      : undefined
  const sortDirection: 'asc' | 'desc' | undefined =
    activeSort && activeSort.direction !== null ? activeSort.direction : undefined

  const dateFrom = toDateOnlyIso(dateRange?.from)
  const dateTo = toDateOnlyIso(dateRange?.to)

  const queryArgs = {
    // Pass the readonly constant directly — `useSageChecks.states` accepts
    // `readonly string[]`, so a spread-copy on every render would be pure
    // GC waste with no type-safety benefit.
    states: OUTSTANDING_CHECK_STATES,
    // DCP-237: the table intentionally shows checks in ALL statuses
    // (Issued, Paid, Voided) — `outstandingOnly` is deliberately omitted
    // so a check stays visible after Date Cashed / status edits. The
    // "Outstanding Checks" KPI tile remains issued-only via its dedicated
    // `sage_checks_outstanding_total` RPC (useBanking.ts), so the tile
    // stays accurate independently of this list.
    facilityIds,
    statuses: selectedStatuses.length ? selectedStatuses : undefined,
    dateFrom,
    dateTo,
  }

  const countQuery = useSageChecksCount(queryArgs)

  // Page and size for the table. Back to page 1 whenever the result set changes
  // meaning — any quick filter (clearing a date included) or the sort. The
  // count is applied through `forTotal` below, after the page query.
  const pagination = useTablePagination({
    totalItems: undefined,
    resetKey: JSON.stringify([dateFrom, dateTo, selectedFacilityIds, selectedStatuses, sortState]),
  })

  const checksQuery = useSageChecks({
    page: pagination.page,
    pageSize: pagination.pageSize,
    sortColumn,
    sortDirection,
    ...queryArgs,
  })
  const rows = checksQuery.data ?? []
  const isLoading = checksQuery.isLoading
  // An empty placeholder is the page left behind by the move to the last page
  // (see `forTotal` below), not an empty result: it shows as loading.
  const tableState = tableQueryState(
    {
      isPending: checksQuery.isPending,
      isPlaceholderData: checksQuery.isPlaceholderData,
      isFetching: checksQuery.isFetching || countQuery.isFetching,
      error: pageOrCountError(checksQuery.error, countQuery.error),
      refetch: () => {
        void checksQuery.refetch()
        void countQuery.refetch()
      },
    },
    rows.length,
  )
  const errorDisplay = queryErrorDisplay(tableState, rows.length)

  /**
   * The total can shrink under the current page without any page action (a
   * status edit drops the last row out of an active Status filter); `forTotal`
   * then moves to the new last page, for good, so a later count increase
   * cannot jump back to the stale page. It moves only on a settled count: the
   * count keeps the previous filter's total as a placeholder, which must not
   * move the page. A garbage count (NaN if the SQL ever changes shape,
   * negative from a future schema bug) is clamped to a non-negative finite
   * number so it cannot become a NaN page count.
   */
  const count = countQuery.data
  const { dataTablePagination } = pagination.forTotal({
    total: count === undefined ? undefined : Number.isFinite(count) ? Math.max(0, count) : 0,
    isFetching: countQuery.isFetching,
  })

  // ============ Derived ============

  const hasActiveFilters =
    !!dateRange || selectedFacilityIds.length > 0 || selectedStatuses.length > 0

  /**
   * Lookup map: financial_entity_id → SageBankAccountRow.
   * Used to render Bank Account # column (and resolve Facility from location_id).
   */
  const bankAccountByEntity = useMemo<Map<string, SageBankAccountRow>>(() => {
    return new Map(bankAccounts.map((ba) => [ba.financial_entity_id, ba]))
  }, [bankAccounts])

  /**
   * Lookup map: facility id → facility name. DCP-308: the Facility column now
   * renders from the check row's denormalized `facility_id` directly, so this
   * keys on the facility PK instead of the old sage_location_id chain.
   */
  const facilityNameById = useMemo<Map<string, string>>(() => {
    return new Map(facilities.map((f) => [f.id, f.name]))
  }, [facilities])

  const facilityOptions = useMemo(
    () =>
      facilities
        .filter((f) => f.is_active)
        .map((f) => ({ value: f.id, label: f.name })),
    [facilities],
  )

  // ============ Mutations & inline edit state ============

  // One mutation writes both DCP-mutable columns (dcp_check_status + date_cashed)
  // atomically — see useUpdateSageCheck / sageChecks.updateCheck (DCP-275).
  const updateCheck = useUpdateSageCheck()

  /**
   * The check currently in inline-edit mode (its `record_no`), or null when no
   * row is being edited. Only one row edits at a time; the draft below holds the
   * uncommitted Status + Date Cashed until the user Saves.
   */
  const [editingRecordNo, setEditingRecordNo] = useState<number | null>(null)
  /** Draft Status while the row is being edited. */
  const [draftStatus, setDraftStatus] = useState<DcpCheckStatus>('issued')
  /** Draft Date Cashed (ISO YYYY-MM-DD) or null while the row is being edited. */
  const [draftDateCashed, setDraftDateCashed] = useState<string | null>(null)

  /**
   * Enter edit mode for a row, seeding the draft from its current effective
   * status and Date Cashed. Triggered by the pencil on EITHER the Status or the
   * Date Cashed cell — both edit the same row's two fields together.
   */
  const beginEdit = useCallback((row: SageCheckRow): void => {
    setEditingRecordNo(row.record_no)
    setDraftStatus(effectiveCheckStatus(row))
    setDraftDateCashed(row.date_cashed)
  }, [])

  /** Discard the draft and leave edit mode without saving. */
  const cancelEdit = useCallback((): void => {
    setEditingRecordNo(null)
  }, [])

  /**
   * Persist the draft Status + Date Cashed in one atomic write. On success the
   * optimistic cache update flips the row and we leave edit mode; on failure the
   * toast surfaces the error and the row stays in edit mode so the operator can
   * correct the input or retry. The error is caught here (not re-thrown) because
   * this is the top-level caller — nothing above needs to react to it.
   */
  const saveEdit = useCallback(
    async (row: SageCheckRow): Promise<void> => {
      try {
        await updateCheck.mutateAsync({
          recordNo: row.record_no,
          status: draftStatus,
          dateCashed: draftDateCashed,
        })
        setEditingRecordNo(null)
      } catch (err) {
        const baseMsg = err instanceof Error ? err.message : 'Failed to update check'
        // Identifier priority mirrors the cell aria-labels: prefer the
        // user-facing Sage check number (`doc_number`), fall back to the
        // internal `record_no` for Approved-state rows that have no DocNumber yet.
        const checkLabel = row.doc_number
          ? `check ${row.doc_number}`
          : `check record ${row.record_no}`
        toast.error(`Failed to update ${checkLabel}: ${baseMsg}`)
      }
    },
    [updateCheck, draftStatus, draftDateCashed],
  )

  /**
   * Whether the current draft cannot be saved, via the shared `canAssignStatus`
   * validator the data layer also uses (so the Save gate can't drift from
   * server-side validation). The date is resolved the same way the server
   * resolves it — only 'paid' carries a date; any other status clears it — so a
   * non-Paid save always passes.
   */
  const saveBlocked = !canAssignStatus(
    draftStatus,
    resolveDateCashed(draftStatus, draftDateCashed),
  ).ok

  /**
   * record_no whose save is currently in flight (drives the cell spinner).
   * Pulled from the mutation's `variables` to avoid a parallel state slot.
   */
  const pendingRecordNo =
    updateCheck.isPending && updateCheck.variables ? updateCheck.variables.recordNo : null

  // ============ Columns ============

  const columns = useMemo<DataTableColumn<SageCheckRow>[]>(
    () => [
      {
        key: 'when_paid',
        header: 'Issued',
        sortable: true,
        render: (row) => {
          // Only Posted checks (STATE='C') reach this table; Sage always
          // populates WHENPAID on Posted rows, so when_paid is the sole source.
          const issuedDate = row.when_paid
          return issuedDate ? formatDate(issuedDate) : '—'
        },
      },
      {
        key: 'facility',
        header: 'Facility',
        sortable: false,
        render: (row) => {
          // DCP-308: render from the denormalized facility_id directly. Falls
          // back to '—' when the check has no facility or the id is unknown.
          if (!row.facility_id) return '—'
          return facilityNameById.get(row.facility_id) ?? '—'
        },
      },
      {
        key: 'bank_account_no',
        header: 'Bank Account #',
        // Joined column — server-side sort isn't supported (would require a
        // PostgREST join); leave unsortable for the table.
        sortable: false,
        render: (row) => {
          // Explicit null branch so an unmapped check (no `financial_entity`
          // yet — happens for Approved-state Sage rows pre-Posting) skips
          // the Map.get('') lookup entirely. Functionally equivalent to the
          // prior `.get(row.financial_entity ?? '')` chain since Map.get('')
          // returns undefined, but the explicit branch reads as the
          // operator's mental model and avoids a needless hash probe.
          if (!row.financial_entity) return '—'
          return bankAccountByEntity.get(row.financial_entity)?.bank_account_no ?? '—'
        },
      },
      {
        key: 'vendor_id',
        header: 'Vendor ID',
        sortable: true,
        render: (row) => row.vendor_id ?? '—',
      },
      // payee is sourced from the Sage VENDOR "Print To" field
      // (DISPLAYCONTACT.PRINTAS), resolved during the check sync via Payee
      // enrichment (fetchVendorPrintAsMap in sage/sync-checks). vendor_name is
      // kept as a fallback for rows that were synced before the payee column
      // was added and have not yet been re-synced.
      {
        key: 'payee',
        header: 'Payee',
        sortable: true,
        render: (row) => row.payee ?? row.vendor_name ?? '—',
      },
      {
        key: 'doc_number',
        header: 'Check #',
        sortable: true,
        // Row identity column — must always stay visible (DCP-279).
        disableHide: true,
        render: (row) => row.doc_number ?? '—',
      },
      {
        key: 'total_paid',
        header: 'Amount',
        sortable: true,
        cellClassName: 'font-medium',
        render: (row) => {
          // Only Posted checks (STATE='C') reach this table; Sage always
          // populates TOTALPAID on Posted rows, so total_paid is the sole source.
          const amount = Number(row.total_paid) || 0
          return formatCurrency(amount)
        },
      },
      {
        key: 'state',
        header: 'Status',
        sortable: true,
        render: (row) => (
          <StatusCell
            row={row}
            isEditing={editingRecordNo === row.record_no}
            draftStatus={draftStatus}
            onDraftStatusChange={setDraftStatus}
            onBeginEdit={beginEdit}
            isPending={pendingRecordNo === row.record_no}
          />
        ),
      },
      {
        key: 'date_cashed',
        header: 'Date Cashed',
        sortable: true,
        // Fixed width so the column does not jump between display (date + pencil)
        // and edit (DatePicker + Save/Cancel) modes — the wider edit layout sets
        // the reserved width (DCP-275). 260px gives the 140px picker plus the two
        // icon buttons breathing room so the edit controls are not crammed against
        // the table's right edge (DCP-289).
        width: '260px',
        minWidth: '260px',
        // Custom render: shares the row-level edit mode with the Status column
        // and owns the Save/Cancel controls (DCP-275). Both fields are edited
        // and saved together via a single atomic mutation.
        render: (row) => (
          <DateCashedCell
            row={row}
            isEditing={editingRecordNo === row.record_no}
            draftStatus={draftStatus}
            draftDateCashed={draftDateCashed}
            onDraftDateCashedChange={setDraftDateCashed}
            onBeginEdit={beginEdit}
            onSave={saveEdit}
            onCancel={cancelEdit}
            saveDisabled={saveBlocked || pendingRecordNo === row.record_no}
            isPending={pendingRecordNo === row.record_no}
          />
        ),
      },
      {
        key: 'synced_at',
        header: 'Import Timestamp',
        sortable: true,
        render: (row) => (row.synced_at ? formatDateTime(row.synced_at) : '—'),
      },
    ],
    [
      bankAccountByEntity,
      facilityNameById,
      editingRecordNo,
      draftStatus,
      draftDateCashed,
      beginEdit,
      saveEdit,
      cancelEdit,
      saveBlocked,
      pendingRecordNo,
    ],
  )

  // DCP-279: per-user column visibility + order, persisted in localStorage.
  const columnPrefs = useTableColumnPrefs('outstanding-checks', columns)

  // ============ Render ============

  /** Reset all quick filters (the page follows through the pagination resetKey). */
  const clearFilters = () => {
    setDateRange(undefined)
    setSelectedFacilityIds([])
    setSelectedStatuses([])
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Outstanding Checks</h1>
        <p className="text-muted-foreground">
          Posted AP checks from Sage Intacct, shown in every status — Issued, Paid and Voided.
        </p>
      </div>

      {/* Aggregated balance tiles (DCP-274) — parity with the other Banking
          tabs. Rendered at the top of the page per the bug's AC#1. */}
      <KpiCards kpi={kpi} loading={kpiLoading} />

      {/* Quick filters: Date Issued + Facility (DCP-133 AC#8) + Status */}
      <div className="flex flex-wrap items-end gap-3">
        {/*
         * Date Issued is split into two independent, clearable DatePickers
         * (From / To) — same pattern as the Banking Dashboard's BankingFilters
         * (DCP-238). The previous single DateRangePicker visually pre-filled
         * the last 30 days and had no clear path. Each picker is optional: an
         * empty From/To simply omits that bound from the query, and clearing
         * both collapses dateRange back to undefined (no date filter → all
         * checks). A light clamp keeps From <= To.
         */}
        <div className="flex flex-col gap-1">
          <label id="oc-from-date-label" className="text-sm mb-1 font-medium">
            Date Issued — From
          </label>
          <DatePicker
            aria-labelledby="oc-from-date-label"
            date={dateRange?.from}
            onDateChange={(date) => {
              setDateRange((prev) => {
                if (!date) {
                  // Clearing From: keep To if set, else drop the filter entirely.
                  return prev?.to ? { from: undefined, to: prev.to } : undefined
                }
                // If From moves past To, pull To up to match.
                const to = prev?.to && date.getTime() > prev.to.getTime() ? date : prev?.to
                return { from: date, to }
              })
            }}
            placeholder="From"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label id="oc-to-date-label" className="text-sm mb-1 font-medium">
            Date Issued — To
          </label>
          <DatePicker
            aria-labelledby="oc-to-date-label"
            date={dateRange?.to}
            onDateChange={(date) => {
              setDateRange((prev) => {
                if (!date) {
                  // Clearing To: keep From if set, else drop the filter entirely.
                  return prev?.from ? { from: prev.from, to: undefined } : undefined
                }
                // If To moves before From, pull From down to match.
                const from = prev?.from && date.getTime() < prev.from.getTime() ? date : prev?.from
                return { from, to: date }
              })
            }}
            placeholder="To"
          />
        </div>
        <div className="min-w-[240px]">
          <MultiSelect
            label="Facility"
            options={facilityOptions}
            value={selectedFacilityIds}
            onChange={setSelectedFacilityIds}
            placeholder="All facilities"
            searchable
            searchPlaceholder="Search facilities..."
          />
        </div>
        <div className="min-w-[200px]">
          <MultiSelect
            label="Status"
            options={STATUS_FILTER_OPTIONS}
            value={toStatusSelectValue(selectedStatuses)}
            onChange={(values) => setSelectedStatuses((prev) => nextStatusSelection(prev, values))}
            placeholder="All"
            // The checked "All" sentinel is the "no filter" state, not a selection to clear.
            clearable={selectedStatuses.length > 0}
          />
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="mb-0.5">
            <X className="mr-1 h-4 w-4" /> Clear filters
          </Button>
        )}
      </div>

      {/* Missing-Sage-mapping warning.
          Surfaced when the active facility filter includes one or more
          facilities with no `sage_location_id`. Without this banner the
          page would show "No outstanding checks match the current filters."
          and the user would assume the data is missing — see the comment
          on `facilitiesWithoutSageMapping` for the configuration-vs-data
          distinction this preserves. Uses the warning (amber) palette so
          it's visually distinct from the destructive (red) error banner
          below and from the muted empty-state copy. */}
      {facilitiesWithoutSageMapping.length > 0 && !isLoading && (
        <div
          role="status"
          className="flex flex-col gap-1 rounded-md border border-amber-500/50 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>
              <strong>
                {facilitiesWithoutSageMapping.length === 1
                  ? `${facilitiesWithoutSageMapping[0]} has`
                  : `${facilitiesWithoutSageMapping.length} selected facilities have`}{' '}
                no Sage Location ID configured
              </strong>{' '}
              — their checks won't appear in this list. Configure the
              mapping in <em>Settings → Facilities</em>{' '}
              {facilitiesWithoutSageMapping.length > 1 && (
                <span className="block mt-1 text-xs opacity-90">
                  Affected facilities: {facilitiesWithoutSageMapping.join(', ')}
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Mapped-but-no-bank-account warning (DCP-308).
          Distinct from the missing-mapping banner above: these facilities DO
          have a Sage Location ID, but no bank account resolves for it
          (case-insensitive), so no checks are attributed to them yet (a check
          gets its facility_id via the bank account at sync time). The fix is a
          data sync, not a config edit — the copy points the operator at
          "Sync Bank Accounts" for the named location. */}
      {facilitiesMappedWithoutBankAccount.length > 0 && !isLoading && bankAccountsReady && (
        <div
          role="status"
          className="flex flex-col gap-1 rounded-md border border-amber-500/50 bg-amber-50 p-4 text-sm text-amber-900"
        >
          {facilitiesMappedWithoutBankAccount.map((f) => (
            <div key={f.id} className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <p>
                Facility <strong>&quot;{f.name}&quot;</strong> is mapped to Sage location{' '}
                <strong>&quot;{f.sageLocationId}&quot;</strong> but no bank account was found for it
                — its checks aren&apos;t synced yet. Run <em>Sync Bank Accounts</em> (then{' '}
                <em>Sync Checks</em>) for that location to pull them.
              </p>
            </div>
          ))}
        </div>
      )}

      {errorDisplay.banner}
      {/* Data table.
          min-h-[680px] is a FLOOR for the empty state and short pages, not
          a target height: loading, error, empty and populated states share
          it, so swapping between them while filtering doesn't snap the
          layout, and it matches the floor of the other paginated /banking/*
          tables. A full page is taller and simply grows the page (it
          scrolls); nothing here is sized to a particular page size. */}
      <DataTable<SageCheckRow>
        className="min-h-[680px]"
        title="Outstanding Checks"
        columns={columns}
        data={rows}
        // Fixed 80px row height applied to EVERY row by design (not
        // just the row being edited) so all rows read at a consistent
        // size in both display mode (date + pencil) and edit mode
        // (DatePicker + Save/Cancel + the "Required for Paid" hint).
        // A per-row conditional height would grow only the editing row
        // and reintroduce the vertical shift DCP-289 removed; a uniform
        // 80px keeps the whole table stable while the inline Status and
        // Date Cashed editors open and close.
        rowClassName="h-20"
        rowKey={(row) => row.record_no}
        sorting={{
          mode: 'server',
          sortState,
          onSortChange: setSortState,
        }}
        // A page that failed keeps the footer, so the user can step back to one that loaded.
        pagination={dataTablePagination}
        itemName="checks"
        enableColumnReorder
        {...columnPrefs}
        loading={tableState.loading}
        refreshing={tableState.refreshing}
        error={errorDisplay.tableError}
        emptyState={
          <p className="text-sm text-muted-foreground">
            {hasActiveFilters
              ? 'No checks match the current filters.'
              : 'No checks. Run Sync Checks from Settings → Sage Intacct.'}
          </p>
        }
      />
    </div>
  )
}
